<template>
  <div>
    <!-- Chart + status INSIDE BaseCard -->
    <BaseCard class="spacing-lg-bottom">
      <privatePensionChart
        :starting-month="dagsFra.getMonth()"
        :movements="SereignHreyfingar.hreyfingar"
        :ret="avoxtunTimabils()"
        :status-end="SereignHreyfingar.lokaStadaTimabils"
        :status-begin="SereignHreyfingar.upphafsStadaTimabils"
        :current-chosen-date="chosenYearStr"
      />
    </BaseCard>

    <div class="table-container spacing-lg-bottom">
      <h2>{{ t('sereignTable_hreyfingar') }}</h2>

      <v-data-table
        :headers="headers"
        :items="SereignHreyfingar.hreyfingar"
        class="m-b-30"
        :loading="loading"
        :disable-pagination="true"
        hide-default-footer
        :loading-text="t('loading')"
        :no-data-text="t('no_data')"
        :footer-props="{ itemsPerPageText: perpage, itemsPerPageAllText: t('show_all') }"
        :mobile-breakpoint="0"
        hover
      >
        <template #item.dags="{ item }">
          {{ formatDate(item.dags) }}
        </template>

        <template #item.gengi="{ item }">
          {{ roundup(item.gengi) }}
        </template>

        <template #item.upphaed="{ item }">
          {{ currency(item.upphaed) }}
        </template>

        <template #item.leid>
          {{ sjodsDeild }}
        </template>

        <!-- opening / yield / closing rows kept as you had -->
        <template #body.prepend>
          <tr v-if="!loading">
            <td class="font-bold">{{ t('sereignTable_stada_upphaf') }}</td>
            <td></td>
            <td />
            <td />
            <td />
            <td class="font-bold text-right">
              {{ currency(SereignHreyfingar.upphafsStadaTimabils) }}
            </td>
          </tr>
        </template>

        <template #body.append>
          <tr v-if="!loading">
            <td class="font-bold">{{ t('sereignTable_avoxtun') }}</td>
            <td></td>
            <td />
            <td />
            <td />
            <td class="font-bold text-right">
              {{ currency(avoxtunTimabils()) }}
            </td>
          </tr>
          <tr v-if="!loading">
            <td class="font-bold">{{ t('sereignTable_stada_lok') }}</td>
            <td>
            </td>
            <td />
            <td />
            <td />
            <td class="text-right font-bold">
              {{ currency(SereignHreyfingar.lokaStadaTimabils) }}
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>

    <div class="m-b-128">
      <h2 class="bold_text">{{ t('subpage_sereign_fyrirvari_title') }}</h2>
      <p>{{ t('subpage_sereign_fyrirvari1') }}</p>
      <p>{{ t('subpage_sereign_fyrirvari2') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatDate } from '@/filters/date'
import { roundup } from '@/filters/roundup'

import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import privatePensionChart from './privatePensionChart.vue'
import BaseCard from '@/components/shared/BaseCard.vue'
import { useCurrency } from '@/composables/useCurrency'

const { currency } = useCurrency()
const { t } = useI18n()

const props = defineProps<{
  rettindiDeilda?: string
  sjodsDeild?: string
  chosenYear: Date | string
  dagsFra: Date
  dagsTil: Date
  SereignHreyfingar: {
    hreyfingar: Array<{ dags: string; upphaed: number; gengi: number }>
    upphafsStadaTimabils: number
    lokaStadaTimabils: number
  }
  loading: boolean
}>()

const chosenYearStr = computed(() =>
  typeof props.chosenYear === 'object'
    ? props.chosenYear.getFullYear().toString()
    : props.chosenYear
)

const perpage = computed(() => t('perpage'))

const headers = computed(() => [
  { text: t('sereignTable_dags'), value: 'dags', sortable: false },
  { text: t('sereignTable_timabil'), value: 'timabil', sortable: false },
  { text: t('sereignTable_leid'), value: 'leid', sortable: false },
  { text: t('sereignTable_gengi'), value: 'gengi' },
  {
    text: t('sereignTable_hreyfingar'),
    value: 'upphaed',
    align: 'start' as const,
    sortable: false
  },
  {
    text: t('sereignTable_stada'),
    value: 'stada',
    align: 'start' as const,
    sortable: false
  }
])

function avoxtunTimabils(): number {
  const { hreyfingar, upphafsStadaTimabils, lokaStadaTimabils } = props.SereignHreyfingar
  if (Array.isArray(hreyfingar) && hreyfingar.length > 0) {
    const totalUpphaed = hreyfingar.reduce((acc, { upphaed }) => acc + upphaed, 0)
    return lokaStadaTimabils - upphafsStadaTimabils - totalUpphaed
  }
  return lokaStadaTimabils - upphafsStadaTimabils
}

// expose props used in template
const sjodsDeild = props.sjodsDeild
const dagsFra = props.dagsFra
const dagsTil = props.dagsTil
</script>

<style scoped lang="scss">
/* removed .sereignTable box-shadow wrapper as requested */

/* keep your existing helpers */
.font-bold { font-weight: bold; }

.privatepensionChart{
  margin: 40px 0;
}
/* optional: table spacing kept as-is via your utility classes */
</style>
